/**
 * Campaign Generation Dashboard
 * Displays AI-generated campaigns from A/B test winners with multi-generational tracking
 */

import React, { useState, useEffect } from 'react';
import { 
  GeneratedCampaign, 
  TemplateEvolution, 
  OptimizedSubjectLine,
  CampaignGenerationService,
  DEFAULT_CAMPAIGN_GENERATION_CONFIG
} from '../../../services/campaignGenerationService';
import { clickable } from "components/ui/interactive";

interface CampaignGenerationDashboardProps {
  templateId?: string;
  onCampaignSelect?: (campaign: GeneratedCampaign) => void;
  onTemplateEvolutionView?: (evolution: TemplateEvolution) => void;
}

export const CampaignGenerationDashboard: React.FC<CampaignGenerationDashboardProps> = ({
  templateId,
  onCampaignSelect,
  onTemplateEvolutionView
}) => {
  const [campaigns, setCampaigns] = useState<GeneratedCampaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<GeneratedCampaign | null>(null);
  const [templateEvolution, setTemplateEvolution] = useState<TemplateEvolution | null>(null);
  const [subjectLines, setSubjectLines] = useState<OptimizedSubjectLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'evolution' | 'subject-lines'>('campaigns');

  const campaignService = new CampaignGenerationService(DEFAULT_CAMPAIGN_GENERATION_CONFIG);

  useEffect(() => {
    loadData();
  }, [templateId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load campaigns
      const generatedCampaigns = campaignService.getGeneratedCampaigns(templateId);
      setCampaigns(generatedCampaigns);

      // Load template evolution if templateId is provided
      if (templateId) {
        const evolution = campaignService.getTemplateEvolution(templateId);
        setTemplateEvolution(evolution);
      }

      // Load subject lines for the first campaign's category
      if (generatedCampaigns.length > 0) {
        const category = generatedCampaigns[0].template.category;
        const optimizedSubjectLines = campaignService.getOptimizedSubjectLines(category);
        setSubjectLines(optimizedSubjectLines);
      }
    } catch (error) {
      console.error('Error loading campaign data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignClick = (campaign: GeneratedCampaign) => {
    setSelectedCampaign(campaign);
    onCampaignSelect?.(campaign);
  };

  const handleEvolutionView = () => {
    if (templateEvolution) {
      onTemplateEvolutionView?.(templateEvolution);
    }
  };

  const _formatPercentage = (value: number) => `${value.toFixed(1)}%`;
  const _formatCurrency = (value: number) => `$${value.toLocaleString()}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-content-secondary">Loading campaign data...</span>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-content">AI Campaign Generation</h2>
            <p className="text-sm text-content-secondary mt-1">
              Campaigns generated from A/B test winners with performance optimization
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              {campaigns.length} Generated
            </span>
            {templateEvolution && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                Gen {templateEvolution.generations.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-line">
        <nav className="-mb-px flex space-x-8 px-6">
          {['campaigns', 'evolution', 'subject-lines'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              {tab === 'campaigns' && 'Generated Campaigns'}
              {tab === 'evolution' && 'Template Evolution'}
              {tab === 'subject-lines' && 'Subject Line Repository'}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'campaigns' && (
          <CampaignsTab 
            campaigns={campaigns}
            selectedCampaign={selectedCampaign}
            onCampaignClick={handleCampaignClick}
          />
        )}
        
        {activeTab === 'evolution' && (
          <EvolutionTab 
            evolution={templateEvolution}
            onViewEvolution={handleEvolutionView}
          />
        )}
        
        {activeTab === 'subject-lines' && (
          <SubjectLinesTab subjectLines={subjectLines} />
        )}
      </div>
    </div>
  );
};

// Campaigns Tab Component
const CampaignsTab: React.FC<{
  campaigns: GeneratedCampaign[];
  selectedCampaign: GeneratedCampaign | null;
  onCampaignClick: (campaign: GeneratedCampaign) => void;
}> = ({ campaigns, selectedCampaign, onCampaignClick }) => {
  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-content-subtle">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="mt-2 text-sm font-medium text-content">No campaigns generated</h3>
        <p className="mt-1 text-sm text-content-muted">
          Campaigns will appear here when A/B test winners are automatically implemented.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Campaign Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {campaigns.map((campaign) => (
          <CampaignCard
            key={campaign.id}
            campaign={campaign}
            isSelected={selectedCampaign?.id === campaign.id}
            onClick={() => onCampaignClick(campaign)}
          />
        ))}
      </div>

      {/* Selected Campaign Details */}
      {selectedCampaign && (
        <div className="mt-8 border-t pt-6">
          <CampaignDetails campaign={selectedCampaign} />
        </div>
      )}
    </div>
  );
};

// Campaign Card Component
const CampaignCard: React.FC<{
  campaign: GeneratedCampaign;
  isSelected: boolean;
  onClick: () => void;
}> = ({ campaign, isSelected, onClick }) => {
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
          : 'border-line hover:border-line-strong hover:shadow-sm'
      }`}
      {...clickable(onClick)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-medium text-content">{campaign.name}</h3>
          <p className="text-sm text-content-secondary mt-1">
            Generation {campaign.generation} • {campaign.template.category}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
            {formatPercentage(campaign.metadata.expectedImprovement)} improvement
          </span>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
            {formatPercentage(campaign.metadata.winnerConfidence)} confidence
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-content-muted">Predicted Open Rate</p>
          <p className="text-sm font-medium text-content">
            {formatPercentage(campaign.performancePrediction.openRate.predicted)}
          </p>
        </div>
        <div>
          <p className="text-xs text-content-muted">Subject Lines</p>
          <p className="text-sm font-medium text-content">
            {campaign.subjectLines.length}
          </p>
        </div>
        <div>
          <p className="text-xs text-content-muted">Segments</p>
          <p className="text-sm font-medium text-content">
            {campaign.audienceSegments.length}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center text-xs text-content-muted">
        <span>Created {campaign.metadata.createdAt.toLocaleDateString()}</span>
        {campaign.parentCampaignId && (
          <span className="ml-2">• Evolved from previous generation</span>
        )}
      </div>
    </div>
  );
};

// Campaign Details Component
const CampaignDetails: React.FC<{ campaign: GeneratedCampaign }> = ({ campaign }) => {
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;
  const formatCurrency = (value: number) => `$${value.toLocaleString()}`;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-content">Campaign Details</h3>

      {/* Performance Prediction */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
        <h4 className="font-medium text-content mb-3">Performance Prediction</h4>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-content-secondary">Open Rate</p>
            <p className="text-lg font-semibold text-content">
              {formatPercentage(campaign.performancePrediction.openRate.predicted)}
            </p>
            <p className="text-xs text-content-muted">
              {formatPercentage(campaign.performancePrediction.openRate.confidence)} confidence
            </p>
          </div>
          <div>
            <p className="text-sm text-content-secondary">Click Rate</p>
            <p className="text-lg font-semibold text-content">
              {formatPercentage(campaign.performancePrediction.clickRate.predicted)}
            </p>
            <p className="text-xs text-content-muted">
              {formatPercentage(campaign.performancePrediction.clickRate.confidence)} confidence
            </p>
          </div>
          <div>
            <p className="text-sm text-content-secondary">Conversion Rate</p>
            <p className="text-lg font-semibold text-content">
              {formatPercentage(campaign.performancePrediction.conversionRate.predicted)}
            </p>
            <p className="text-xs text-content-muted">
              {formatPercentage(campaign.performancePrediction.conversionRate.confidence)} confidence
            </p>
          </div>
          <div>
            <p className="text-sm text-content-secondary">Revenue Impact</p>
            <p className="text-lg font-semibold text-content">
              {formatCurrency(campaign.performancePrediction.revenueImpact.predicted)}
            </p>
            <p className="text-xs text-content-muted">
              {campaign.performancePrediction.revenueImpact.timeframe}
            </p>
          </div>
        </div>
      </div>

      {/* Subject Lines */}
      <div>
        <h4 className="font-medium text-content mb-3">Optimized Subject Lines</h4>
        <div className="space-y-2">
          {campaign.subjectLines.slice(0, 3).map((subjectLine) => (
            <div key={subjectLine.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-700 rounded">
              <div>
                <p className="font-medium text-content">{subjectLine.text}</p>
                <div className="flex items-center space-x-4 mt-1">
                  <span className="text-xs text-content-muted">
                    {formatPercentage(subjectLine.performance.predictedOpenRate)} predicted open rate
                  </span>
                  {subjectLine.emotionalTriggers.length > 0 && (
                    <span className="text-xs text-blue-600 dark:text-blue-300">
                      {subjectLine.emotionalTriggers.map(t => t.type).join(', ')}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-xs text-content-muted">
                {formatPercentage(subjectLine.performance.confidence)} confidence
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Audience Segments */}
      {campaign.audienceSegments.length > 0 && (
        <div>
          <h4 className="font-medium text-content mb-3">Audience Segments</h4>
          <div className="space-y-2">
            {campaign.audienceSegments.map((segment) => (
              <div key={segment.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-700 rounded">
                <div>
                  <p className="font-medium text-content">{segment.name}</p>
                  <p className="text-sm text-content-secondary">
                    {segment.size.toLocaleString()} users • 
                    {formatPercentage(segment.expectedPerformance.openRate)} expected open rate
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-content">
                    {segment.optimizations.length} optimizations
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Factors & Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {campaign.performancePrediction.riskFactors.length > 0 && (
          <div>
            <h4 className="font-medium text-content mb-3">Risk Factors</h4>
            <div className="space-y-2">
              {campaign.performancePrediction.riskFactors.map((risk, index) => (
                <div key={index} className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded">
                  <p className="font-medium text-red-900 dark:text-red-300 capitalize">
                    {risk.type.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{risk.mitigation}</p>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-red-600 dark:text-red-300">
                    <span>{formatPercentage(risk.probability)} probability</span>
                    <span>{formatPercentage(risk.impact)} impact</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {campaign.performancePrediction.optimizationOpportunities.length > 0 && (
          <div>
            <h4 className="font-medium text-content mb-3">Optimization Opportunities</h4>
            <div className="space-y-2">
              {campaign.performancePrediction.optimizationOpportunities.map((opportunity, index) => (
                <div key={index} className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded">
                  <p className="font-medium text-green-900 dark:text-green-300 capitalize">
                    {opportunity.type}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">{opportunity.description}</p>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-green-600 dark:text-green-300">
                    <span>{formatPercentage(opportunity.expectedImprovement)} improvement</span>
                    <span>{opportunity.effort} effort</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Evolution Tab Component
const EvolutionTab: React.FC<{
  evolution: TemplateEvolution | null;
  onViewEvolution: () => void;
}> = ({ evolution, onViewEvolution }) => {
  if (!evolution) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-content-subtle">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <h3 className="mt-2 text-sm font-medium text-content">No evolution data</h3>
        <p className="mt-1 text-sm text-content-muted">
          Template evolution will be tracked as campaigns are generated from A/B test winners.
        </p>
      </div>
    );
  }

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  return (
    <div className="space-y-6">
      {/* Evolution Overview */}
      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-content">Evolution Overview</h4>
          <button
            onClick={onViewEvolution}
            className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300"
          >
            View Full Evolution →
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-content-secondary">Generations</p>
            <p className="text-2xl font-semibold text-content">{evolution.generations.length}</p>
          </div>
          <div>
            <p className="text-sm text-content-secondary">Evolution Steps</p>
            <p className="text-2xl font-semibold text-content">{evolution.evolutionPath.length}</p>
          </div>
          <div>
            <p className="text-sm text-content-secondary">Learnings</p>
            <p className="text-2xl font-semibold text-content">{evolution.learnings.length}</p>
          </div>
        </div>
      </div>

      {/* Performance Trajectory */}
      <div>
        <h4 className="font-medium text-content mb-3">Performance Trajectory</h4>
        <div className="space-y-3">
          {evolution.performanceTrajectory.map((point, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-surface border rounded">
              <div>
                <p className="font-medium text-content">Generation {point.generation}</p>
                <p className="text-sm text-content-secondary">{point.timestamp.toLocaleDateString()}</p>
              </div>
              <div className="grid grid-cols-4 gap-4 text-right">
                <div>
                  <p className="text-xs text-content-muted">Open Rate</p>
                  <p className="text-sm font-medium">{formatPercentage(point.metrics.openRate)}</p>
                </div>
                <div>
                  <p className="text-xs text-content-muted">Click Rate</p>
                  <p className="text-sm font-medium">{formatPercentage(point.metrics.clickRate)}</p>
                </div>
                <div>
                  <p className="text-xs text-content-muted">Conversion</p>
                  <p className="text-sm font-medium">{formatPercentage(point.metrics.conversionRate)}</p>
                </div>
                <div>
                  <p className="text-xs text-content-muted">Revenue</p>
                  <p className="text-sm font-medium">${point.metrics.revenue.toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Learnings */}
      {evolution.learnings.length > 0 && (
        <div>
          <h4 className="font-medium text-content mb-3">Key Learnings</h4>
          <div className="space-y-2">
            {evolution.learnings.slice(0, 3).map((learning) => (
              <div key={learning.id} className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 rounded">
                <p className="font-medium text-blue-900 dark:text-blue-300 capitalize">{learning.type}</p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">{learning.description}</p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-2">
                  {formatPercentage(learning.confidence)} confidence • 
                  Applicable to: {learning.applicability.join(', ')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Subject Lines Tab Component
const SubjectLinesTab: React.FC<{ subjectLines: OptimizedSubjectLine[] }> = ({ subjectLines }) => {
  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  if (subjectLines.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-12 w-12 text-content-subtle">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1.586l-4 4z" />
          </svg>
        </div>
        <h3 className="mt-2 text-sm font-medium text-content">No subject lines optimized</h3>
        <p className="mt-1 text-sm text-content-muted">
          Optimized subject lines will appear here as campaigns are generated.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-content">Optimized Subject Lines</h4>
        <span className="text-sm text-content-secondary">{subjectLines.length} total</span>
      </div>

      <div className="space-y-3">
        {subjectLines.map((subjectLine) => (
          <div key={subjectLine.id} className="border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-medium text-content">{subjectLine.text}</p>
                <div className="flex items-center space-x-4 mt-2">
                  <span className="text-sm text-content-secondary">
                    {formatPercentage(subjectLine.performance.predictedOpenRate)} predicted open rate
                  </span>
                  <span className="text-sm text-content-secondary">
                    {formatPercentage(subjectLine.performance.confidence)} confidence
                  </span>
                  {subjectLine.performance.historicalPerformance && (
                    <span className="text-sm text-green-600 dark:text-green-300">
                      {formatPercentage(subjectLine.performance.historicalPerformance.openRate)} actual
                    </span>
                  )}
                </div>
              </div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200">
                {subjectLine.category}
              </span>
            </div>

            {/* Emotional Triggers */}
            {subjectLine.emotionalTriggers.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-content-muted mb-1">Emotional Triggers:</p>
                <div className="flex flex-wrap gap-1">
                  {subjectLine.emotionalTriggers.map((trigger, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                    >
                      {trigger.type} ({formatPercentage(trigger.strength)})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Personalization Tokens */}
            {subjectLine.personalizationTokens.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-content-muted mb-1">Personalization:</p>
                <div className="flex flex-wrap gap-1">
                  {subjectLine.personalizationTokens.map((token, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                    >
                      {token}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Variations */}
            {subjectLine.variations.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-content-muted mb-1">Variations:</p>
                <div className="text-sm text-content-secondary space-y-1">
                  {subjectLine.variations.slice(0, 2).map((variation, index) => (
                    <p key={index}>• {variation}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignGenerationDashboard;